import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { ATLAS_CAP } from '@infras/shared-types';
import {
  ATLAS_UNMAPPED,
  PROJECTS_OVERVIEW,
  capacity,
  type AtlasProjectOverview,
  type UnmappedCluster,
} from '../lib/atlas';
import { ATLAS_EGRESS_IPS, isEgressQueryUnavailable, type AtlasEgressIp } from '../lib/egress';
import {
  AGENT_CHECKLIST,
  DRIFT_FILTER,
  RED_FILTER,
  REG_FAILED_FILTER,
  formatWindow,
  openToAllLineKind,
  overviewLineClass,
  type AgentChecklist,
  type SupersededSuppressionSummary,
} from '../lib/agentLifecycle';
import { timeAgo } from '../lib/agentCommands';
import { RefreshButton } from '../components/RefreshButton';

const COUNTS = gql`
  query NavCounts {
    navCounts {
      repos
      clusters
      secrets
      unownedRepos
      unownedClusters
      totalAgents
      goneAgentsHoldingIp
    }
  }
`;

/**
 * The Overview: how big the estate is, and what in it needs attention.
 *
 * Deliberately ONE section. It previously also carried scanner-freshness cards, a
 * waiting-on-you band and a change-events feed; those were removed rather than
 * hidden, because each already has a page that owns it (/scheduler, /access,
 * /activity) and a second copy is a second thing to keep true. The freshness pill
 * in the top bar still reports scanner health on every page.
 *
 * Routed at /overview. `/` is still a redirect (Get Started on a first visit,
 * /repos after), so this is a page you navigate to, not the one that greets you.
 *
 * The counts come from `navCounts` — including `unownedRepos`/`unownedClusters`,
 * which exist on that resolver for this page specifically (/repos and /clusters
 * compute their own from their rows).
 */
export function OverviewPage() {
  const { data: countData } = useQuery<{
    navCounts: {
      repos: number;
      clusters: number;
      secrets: number;
      unownedRepos: number;
      unownedClusters: number;
      totalAgents: number;
      goneAgentsHoldingIp: number;
    };
  }>(COUNTS);

  const counts = countData?.navCounts;

  // ONE query for the silent count, shared by the glance card and the checklist below.
  // Two queries would be two answers to "which agents have stopped reporting" on the
  // same screen — the exact defect the single clock exists to prevent. `errorPolicy`
  // for the D11 deploy-skew reason the checklist documents.
  const checklist = useQuery<{ agentChecklist: AgentChecklist }>(AGENT_CHECKLIST, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const redAgents = checklist.data?.agentChecklist.redAgents;

  return (
    <div className="page stack">
      <div className="detail-head">
        <h2>Overview</h2>
      </div>

      <section>
        <h3 className="section">Inventory at a glance</h3>
        <div className="glance-row">
          {(
            [
              ['Repos', counts?.repos, '/repos'],
              ['Clusters', counts?.clusters, '/clusters'],
              ['Secrets', counts?.secrets, '/secrets'],
            ] as [string, number | undefined, string][]
          ).map(([label, n, to]) => (
            <Link className="glance-card" to={to} key={label}>
              <div className="glance-card__n">{n ?? '—'}</div>
              <div className="glance-card__label">{label}</div>
            </Link>
          ))}
          {/* Nodes that have stopped reporting. A card rather than a line in the notes
              below because this one grows on its own — autoscaling retires nodes every
              day — so it needs a place that reads the same whether it says 0 or 186.
              The number is the checklist's red count, from the SAME query and the same
              clock as the line below and as the fleet list it links to. */}
          {redAgents != null && (
            <Link
              className={`glance-card${redAgents.count > 0 ? ' glance-card--bad' : ''}`}
              to={`/cluster-access/fleet?status=${encodeURIComponent(RED_FILTER)}`}
              title={`Agents that have sent no heartbeat for more than ${redAgents.thresholdMinutes} min, out of ${counts?.totalAgents ?? '?'} in the fleet`}
            >
              <div className="glance-card__n">{redAgents.count}</div>
              <div className="glance-card__label">Silent nodes</div>
            </Link>
          )}
        </div>
        {/* Phase 6 §3: an absent owner IS the finding, and a count is what makes
            anyone act on it — an empty column in a table gets scrolled past. */}
        {counts && counts.unownedRepos > 0 && (
          <p className="unowned-note">
            <strong>{counts.unownedRepos}</strong> of {counts.repos} repos have no owner.{' '}
            <Link to="/repos?noowner=1">Review them</Link> — an owner is the person a question or
            an incident goes to.
          </p>
        )}
        {/* Clusters get their own line rather than one combined total: the two
            are fixed by different people, and an unowned production cluster is
            the more urgent of the two — a sum would bury it. */}
        {counts && counts.unownedClusters > 0 && (
          <p className="unowned-note">
            <strong>{counts.unownedClusters}</strong> of {counts.clusters} clusters have no owner.{' '}
            <Link to="/clusters?sort=owner">Review them</Link> — unowned rows sort to the bottom.
          </p>
        )}
        {/* How many of the silent nodes are still costing something. The count of
            silent nodes is the card above and the checklist line below; this sentence
            adds only the part an operator acts on — the slots. */}
        {counts && counts.goneAgentsHoldingIp > 0 && (
          <p className="unowned-note">
            <strong>{counts.goneAgentsHoldingIp}</strong> silent node
            {counts.goneAgentsHoldingIp === 1 ? '' : 's'} still hold an Atlas access-list address,
            and each project caps at {ATLAS_CAP}.{' '}
            <Link to="/cluster-access">Reclaim the slots</Link>.
          </p>
        )}
      </section>

      <NotificationChecklist checklist={checklist.data?.agentChecklist} />
      <SupersededNotice
        summary={checklist.data?.agentChecklist.suppressionSuperseded}
        refetch={() => checklist.refetch().catch(() => undefined)}
      />
    </div>
  );
}

/**
 * The Notification checklist — a CHECKLIST, not a feed.
 *
 * Every line is one open item with a count and a deep link, and it disappears when
 * the item is done. There is no paging behind it and no Slack wiring in front of it
 * (owner decision, 2026-08-24): these lines wait here for the next person who opens
 * the app, which is the whole reason the section MUST be able to render empty. A
 * checklist that always has something on it is a checklist nobody reads, and here
 * there is no second channel behind it.
 *
 * So: no green ticks, no "all clear" placeholder, no heading when there is nothing
 * to say — `items.length === 0` renders NOTHING (AC-16).
 *
 * THIS IS WHERE DEVOPS ACTS (owner decision, Binh, 2026-08-25). The red line is the
 * one the fleet is watched from: an agent silent past the red threshold shows up here,
 * and the operator either recovers the node or removes the agent. Nothing else about
 * an agent's lifecycle reaches this page.
 *
 * AND IT IS NOW WHERE A `/0` IS FOUND (2026-08-26). The Cluster access screen's 458-row
 * Egress table was removed as unused, and it was the only surface that rendered an
 * access-list entry open to the internet. Moving that finding here — pinned above every
 * other line — is the condition of the removal, not a nice-to-have alongside it: an
 * alarm that lives only in a table nobody opens is not an alarm.
 *
 * EVERY ITEM HERE IS OPEN WORK. There was briefly an informational line (a self-heal repair
 * that had already happened); it went when the owner ruled out automatic repair, because a
 * checklist that mixes "someone must act" with "for your information" trains people to skim
 * both. That rule still holds, and it is why the superseded-removal notice is NOT a line
 * here: it is a separate section below, with its own class and no deep link — see
 * `SupersededNotice`.
 *
 * WHAT IS DELIBERATELY NOT HERE:
 *   • The warning state (silent > 10 min). Page-local to the fleet list by decision —
 *     at this scale the line would never be absent, and the section would stop being
 *     read.
 *   • The silent-nodes-holding-an-address sentence. It is the paragraph above, kept
 *     where it already lives: a second copy on the same page is the exact thing this
 *     page's header forbids. Its deep link and its ATLAS_CAP sentence are unchanged.
 *   • A change feed. /activity owns history — see the page header.
 */
function NotificationChecklist({ checklist }: { checklist?: AgentChecklist }) {
  const projects = useQuery<{ atlasProjectsOverview: AtlasProjectOverview[] }>(PROJECTS_OVERVIEW, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const unmapped = useQuery<{ atlasUnmappedClusters: UnmappedCluster[] }>(ATLAS_UNMAPPED, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  // Same D11 tolerance the Cluster access screen carries: the web bundle deploys
  // ahead of the API on every merge, so a query the running API has never heard of
  // must degrade to "one fewer line", never to a blank Overview.
  const egress = useQuery<{ atlasEgressIps: AtlasEgressIp[] }>(ATLAS_EGRESS_IPS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const items = useMemo<ChecklistItem[]>(() => {
    const out: ChecklistItem[] = [];

    // ── Open to the internet ──────────────────────────────────────────────────
    //
    // THE TOP LINE, pinned above every other item rather than sorted with them: the
    // severity-then-count rule would put a 196-of-200 capacity warning above a single
    // `/0`, and a list that admits everything is not "nearly full", it is open.
    //
    // This line is the CONDITION of the Egress table's removal (2026-08-26). A `/0` used
    // to be one row in 458 behind an entry-state filter nobody set; it is now a sentence
    // where DevOps already looks.
    //
    // ZERO HAS TWO MEANINGS, so one item covers both: red when we found `/0` entries,
    // amber when we merely could not look. Both empty renders nothing, exactly like every
    // other line (AC-16).
    //
    // It does NOT offer a removal, because this app has none: `open_to_all` is outside
    // `ATLAS_RECLAIMABLE_STATES` and `prune.ts` refuses it by name, so the project drawer
    // renders no checkbox on that row. The link goes to the entry in context — the
    // project's access list, filtered to the address, where the Atlas comment usually
    // records who opened it — and the sentence says the removal happens in Atlas.
    const openToAll = checklist?.openToAll;
    const openKind = openToAllLineKind(openToAll);
    if (openToAll && openKind) {
      const rows = openToAll.rows;
      const projectKeys = [...new Set(rows.map((r) => r.atlasProjectKey))];
      // One row → straight to it. `liveq` is the access-list table's own search param, so
      // the drawer opens with that address the only row on screen. More than one row in
      // one project → the project. Spread across projects → the screen that lists them.
      const to =
        rows.length === 1
          ? `/cluster-access/project/${encodeURIComponent(projectKeys[0])}?liveq=${encodeURIComponent(rows[0].ipCidr)}`
          : projectKeys.length === 1
            ? `/cluster-access/project/${encodeURIComponent(projectKeys[0])}`
            : '/cluster-access';
      const unreadable = openToAll.unreadableProjects;
      // PROVENANCE, ON THE LINE AND NOT ONLY ON THE WIRE. `listReadAt` is served from the
      // STORED reading (047), and nothing refreshes that on a schedule: `listEntries` and
      // the drawer's Refresh write it, `atlas-drift-detect` reads Atlas without storing
      // it, and the reconcile job is off. So this alarm can outlive the entry that raised
      // it, and its silence is only as current as the last time someone opened the
      // project. A line that shows the finding but not its age asserts a freshness the
      // field never claimed. The OLDEST reading is the one printed — the weakest claim
      // the line can make, which is the safe direction for a security control.
      const oldestRead = [...rows].sort(
        (a, b) => new Date(a.listReadAt).getTime() - new Date(b.listReadAt).getTime(),
      )[0]?.listReadAt;
      out.push({
        id: 'open-to-all',
        pinned: true,
        severity: openKind === 'open-red' ? 'red' : 'amber',
        count: openToAll.count,
        to,
        linkLabel:
          openToAll.count === 0
            ? 'Open cluster access'
            : rows.length === 1
              ? 'Open the entry'
              : projectKeys.length === 1
                ? 'Open the project'
                : 'Open cluster access',
        // Which entries, and their Atlas comments — the checklist is one sentence per
        // item, and this is the cheapest way to answer "which ones" without a screen.
        title: rows.length
          ? rows
              .map(
                (r) =>
                  `${r.ipCidr} · ${r.atlasProjectKey}` +
                  (r.comment ? ` · "${r.comment}"` : ' · no comment in Atlas'),
              )
              .join('\n')
          : undefined,
        body:
          openToAll.count > 0 ? (
            <>
              <strong>{openToAll.count}</strong> access-list entr
              {openToAll.count === 1 ? 'y admits' : 'ies admit'} the whole internet
              {projectKeys.length === 1 ? (
                <>
                  {' '}
                  in <strong>{projectKeys[0]}</strong>
                </>
              ) : (
                <>
                  {' '}
                  across {projectKeys.length} project{projectKeys.length === 1 ? '' : 's'}
                </>
              )}
              . Every other entry in {openToAll.count === 1 ? 'that list' : 'those lists'} is
              decoration while {openToAll.count === 1 ? 'it' : 'they'} stand
              {openToAll.count === 1 ? 's' : ''}. This app will not remove a{' '}
              <code>/0</code> — it is a decision, not waste — so the removal is made in
              Atlas.
              {oldestRead && (
                <>
                  {' '}
                  {projectKeys.length === 1 ? 'That list was' : 'The oldest of those lists was'}{' '}
                  last read {timeAgo(oldestRead)}; this line never re-reads Atlas.
                </>
              )}
              {unreadable.length > 0 && (
                <>
                  {' '}
                  {unreadable.length} more project{unreadable.length === 1 ? '' : 's'} could not
                  be checked at all ({unreadable.join(', ')}).
                </>
              )}
            </>
          ) : (
            <>
              <strong>{unreadable.length}</strong> Atlas project
              {unreadable.length === 1 ? "'s" : "s'"} access list
              {unreadable.length === 1 ? ' has' : 's have'} never been read, so nothing here
              can tell you whether {unreadable.length === 1 ? 'it is' : 'they are'} open to the
              internet ({unreadable.join(', ')}). No <code>/0</code> was found in the lists we
              do have.
            </>
          ),
      });
    }

    // The server ships the count, the cluster count AND the threshold it counted at.
    // None of the three is re-derived here: a checklist that measured red with its own
    // clock would disagree with the fleet list it links to.
    const redAgents = checklist?.redAgents;
    if (redAgents && redAgents.count > 0) {
      out.push({
        id: 'red-agents',
        severity: 'red',
        count: redAgents.count,
        to: `/cluster-access/fleet?status=${encodeURIComponent(RED_FILTER)}`,
        linkLabel: 'See them',
        body: (
          <>
            <strong>{redAgents.count}</strong> agent{redAgents.count === 1 ? '' : 's'} have sent
            no heartbeat for more than {redAgents.thresholdMinutes} minutes, across{' '}
            {redAgents.clusterCount} cluster{redAgents.clusterCount === 1 ? '' : 's'}. Recover the
            node, or remove the agent to reclaim its Atlas address. Nothing on Atlas has changed
            on its own.
          </>
        ),
      });
    }

    // ── Atlas drift: live nodes that cannot reach the database ────────────────
    //
    // A row is an address a HEARTBEATING agent owns that Atlas does not have. This
    // replaced the self-heal pair of lines (armed notice / un-armed warning) when the
    // owner ruled out automatic repair: the job DETECTS and a human REPAIRS, so there is
    // no armed state to distinguish and there is nothing informational about it. One
    // line, red, and it stays until someone restores the addresses.
    //
    // count 0 renders nothing, like every other line (AC-16).
    const drift = checklist?.atlasDrift;
    if (drift && drift.count > 0) {
      // Straight to the affected agents, with the restore action on the page. `__drift__`
      // is the same kind of sentinel as `__regfailed__` — the condition lives on an Atlas
      // row, not on `agents.status`, so the fleet list cannot filter for it unaided.
      out.push({
        id: 'atlas-drift',
        severity: 'red',
        count: drift.count,
        to: `/cluster-access/fleet?status=${encodeURIComponent(DRIFT_FILTER)}`,
        linkLabel: 'Review and restore',
        // The addresses themselves, on hover. The checklist is one sentence per item by
        // design, and this is the cheapest way to answer "which ones" without a screen.
        title: drift.rows
          .map(
            (r) =>
              `${r.ipCidr} · ${r.clusterName} → ${r.atlasProjectKey}` +
              (r.prunedBy ? ` · removed by ${r.prunedBy}` : ' · no removal on record'),
          )
          .join('\n'),
        body: (
          <>
            <strong>{drift.count}</strong> live agent
            {drift.count === 1 ? "'s address is" : "s' addresses are"} missing from Atlas —{' '}
            {drift.count === 1 ? 'that node cannot' : 'those nodes cannot'} reach the database.
            Nothing puts {drift.count === 1 ? 'it' : 'them'} back on its own
            {drift.windowHours ? `; detected in the last ${drift.windowHours} h` : ''}.
          </>
        ),
      });
    }

    // `is_whitelisted = false` with an error: the node's address never reached the
    // access list, so its workloads cannot reach the database. Today this is the
    // shape a cap-full outage arrives in, and it is visible nowhere else.
    const failures = checklist?.failedRegistrations;
    if (failures && failures.count > 0) {
      // The SERVER's count, not one derived from `rows`: that list is capped at 200, and a
      // cap-full event is precisely when it overflows — the line would then pin at the
      // limit on the one item whose job is to size the outage in progress.
      const agents = failures.count;
      out.push({
        id: 'failed-registrations',
        severity: 'red',
        count: agents,
        to: `/cluster-access/fleet?status=${encodeURIComponent(REG_FAILED_FILTER)}`,
        linkLabel: 'See them',
        body: (
          <>
            <strong>{agents}</strong> agent{agents === 1 ? '' : 's'} could not be whitelisted — the
            address never reached the Atlas access list, so those nodes cannot reach the database.
          </>
        ),
      });
    }

    // Thresholds from `capacity()` → shared-types, never a number written here: a page that warns
    // at a different figure than the API classifies at is two claims about whether
    // production is about to lose database access.
    // Per kind (e-10): an Atlas project against 200, a DigitalOcean firewall against 100
    // — `capacity()` reads both from shared-types. A DO row at 300 is a red line about
    // ITS cap, never an Atlas one (AC-27).
    for (const p of projects.data?.atlasProjectsOverview ?? []) {
      const c = capacity(p.liveCount, p.pendingCount, p.kind);
      const level = c.level;
      if (level !== 'warn' && level !== 'crit') continue;
      const isDo = c.kind === 'do_db';
      out.push({
        id: `cap-${p.projectKey}`,
        severity: level === 'crit' ? 'red' : 'amber',
        count: p.liveCount ?? 0,
        to: `/cluster-access/project/${encodeURIComponent(p.projectKey)}`,
        linkLabel: 'Open the target',
        body: (
          <>
            <strong>{p.displayName || p.projectKey}</strong> is at {p.liveCount} of {c.cap}{' '}
            {isDo ? 'DO firewall rules' : 'access-list entries'}
            {level === 'crit'
              ? ` — past ${c.crit}. The next node to come up may be refused outright.`
              : ` — past ${c.warn}. There is still room to clean up before adds start failing.`}
          </>
        ),
      });
    }

    // Only meaningful while nothing reclaims automatically — which is today: the
    // reclaim job does not exist yet. When it ships, this line has to be conditioned
    // on the job being off or frozen, or it becomes noise about scheduled work.
    if (!isEgressQueryUnavailable(egress.error)) {
      // A do_db target's entries are never prune fodder (the server refuses every
      // delete for it), so they do not count towards a line whose link is the prune.
      const doKeys = new Set(
        (projects.data?.atlasProjectsOverview ?? [])
          .filter((p) => p.kind === 'do_db')
          .map((p) => p.projectKey),
      );
      const reclaimable = (egress.data?.atlasEgressIps ?? []).reduce(
        (n, r) => n + r.projects.filter((p) => p.reclaimable && !doKeys.has(p.projectKey)).length,
        0,
      );
      if (reclaimable > 0) {
        out.push({
          id: 'reclaimable',
          severity: 'amber',
          count: reclaimable,
          // WAS `/cluster-access?iponly=1` — the "Reclaimable only" checkbox on the Egress
          // table, which went on 2026-08-26. The param would have been silently ignored,
          // which is the worst kind of dead link: it still loads. Reclaimable addresses
          // span projects and no surviving screen aggregates them, so this lands on the
          // project list, where each drawer's "No agent" / "Stale" filters are the same
          // two states `reclaimable` is made of — and carry the removal.
          to: '/cluster-access',
          linkLabel: 'Open the projects',
          body: (
            <>
              <strong>{reclaimable}</strong> access-list address
              {reclaimable === 1 ? '' : 'es'} can be reclaimed now. Nothing reclaims them on its
              own — every removal is a human preview and confirm.
            </>
          ),
        });
      }
    }

    const orphanClusters = (unmapped.data?.atlasUnmappedClusters ?? []).filter((c) => !c.ignored);
    if (orphanClusters.length) {
      out.push({
        id: 'unmapped',
        severity: 'amber',
        count: orphanClusters.length,
        to: '/cluster-access/mappings',
        linkLabel: 'Map them',
        body: (
          <>
            <strong>{orphanClusters.length}</strong> cluster
            {orphanClusters.length === 1 ? ' runs' : 's run'} agents with no Atlas project mapped, so
            their addresses are pushed nowhere.
          </>
        ),
      });
    }

    // Pinned first, then severity, then the bigger number — an operator reading top to
    // bottom should meet the thing that breaks production before the thing that is untidy.
    //
    // `pinned` exists for exactly one line and should stay that way. It is not "more
    // important"; it is the one item the count comparison ranks WRONGLY, because its
    // number (1) and a capacity warning's number (196) are not the same kind of quantity.
    // A second pinned item would put two lines in an unordered pair at the top and the
    // rule would be back to arbitrary.
    const rank = (s: Severity) => (s === 'red' ? 0 : 1);
    return out.sort(
      (a, b) =>
        Number(Boolean(b.pinned)) - Number(Boolean(a.pinned)) ||
        rank(a.severity) - rank(b.severity) ||
        b.count - a.count,
    );
  }, [checklist, projects.data, unmapped.data, egress.data, egress.error]);

  if (!items.length) return null;

  return (
    <section>
      <h3 className="section">Needs attention</h3>
      {items.map((i) => (
        <p
          key={i.id}
          className={overviewLineClass(i.severity === 'red' ? 'open-red' : 'open-amber')}
          title={i.title}
        >
          {i.body} <Link to={i.to}>{i.linkLabel}</Link>.
        </p>
      ))}
    </section>
  );
}

type Severity = 'red' | 'amber';

interface ChecklistItem {
  id: string;
  severity: Severity;
  /** What the line counts. Used for ordering within a severity, so it has to be the
   *  number the sentence states, not a proxy for it. */
  count: number;
  /** Sorts above everything, ignoring severity and count. One line only — see the sort. */
  pinned?: boolean;
  to: string;
  linkLabel: string;
  /** Optional hover detail — the rows behind the sentence, when naming them is the only
   *  way an operator can act on the line. */
  title?: string;
  body: ReactNode;
}

/**
 * Superseded removals — A NOTICE, NOT AN ALARM, AND NOT OPEN WORK.
 *
 * WHY IT IS ITS OWN SECTION. The checklist above is defined as open items only, and
 * the one way this feature can go wrong is being read as a failure. Nothing failed
 * here: a person removed an address, a node that is still running reported it again,
 * and the address is effective again — which is the correct outcome, because a live
 * node must not stay locked out of the database. There is no resolve action, so there
 * is nothing for a deep link to land on and nothing for the line to disappear when.
 * Putting it on the checklist would have meant either an item nobody can tick off, or
 * an alarm about a system working as intended.
 *
 * SO THE DISTINCTION IS STRUCTURAL, NOT JUST A COLOUR: a separate heading that says
 * "for the record", `overviewLineClass('notice')` rather than either checklist class,
 * no `<Link>`, no count card, and past-tense wording throughout. Red is reserved for
 * `atlasDrift`, which is the same three provenance fields describing the OPPOSITE
 * situation — an address a live agent needs that Atlas does not have.
 *
 * WHY IT EXISTS AT ALL. On prod, 2026-08-26, the owner removed socket-io-v1's Atlas
 * addresses; the agent re-registered minutes later and the write path nulled
 * `pruned_at`/`pruned_by`/`pruned_reason`, so every trace of his removal was erased and
 * the app could only tell him no removal had ever happened. Migration 051 preserves
 * those facts; this section is where a human reads them. The row therefore has to name
 * WHO removed it, WHEN and WHY — not merely that something was superseded.
 *
 * count 0 renders nothing — no heading, no "all clear" — the same rule the checklist
 * follows (AC-16). The empty state is the ordinary one.
 *
 * The window is the server's `windowHours` (7 days, longer than drift's 6 h because
 * nothing ages this fact out), printed through `formatWindow`, never a literal here.
 */
function SupersededNotice({
  summary,
  refetch,
}: {
  summary?: SupersededSuppressionSummary;
  refetch: () => unknown;
}) {
  if (!summary || summary.count < 1) return null;
  const one = summary.count === 1;
  return (
    <section>
      <div className="section-head">
        <h3>For the record</h3>
        <span className="spacer" />
        <RefreshButton refetch={refetch} />
      </div>
      <p className={overviewLineClass('notice')}>
        <strong>{summary.count}</strong> address{one ? '' : 'es'} a person had removed{' '}
        {one ? 'was' : 'were'} brought back by a still-running node in the last{' '}
        {formatWindow(summary.windowHours)}. Nothing is wrong — {one ? 'the node' : 'those nodes'}{' '}
        needed {one ? 'it' : 'them'}, and {one ? 'it is' : 'they are'} on the access list again.
        There is nothing to do here; it is recorded because the removal used to leave no trace at
        all.
      </p>
      <div className="atlas-card notice-note__rows">
        <table className="atlas-table">
          <thead>
            <tr>
              <th scope="col">Address</th>
              <th scope="col">Project</th>
              <th scope="col">Cluster / node</th>
              <th scope="col">Removed by</th>
              <th scope="col">Brought back</th>
            </tr>
          </thead>
          <tbody>
            {summary.rows.map((r) => (
              <tr key={`${r.atlasProjectKey}|${r.ipCidr}`}>
                <td className="mono atlas-cidr">{r.ipCidr}</td>
                <td>{r.atlasProjectKey}</td>
                <td>
                  <div>{r.clusterName || 'cluster not recorded'}</div>
                  {r.nodeName && <div className="atlas-sub">{r.nodeName}</div>}
                </td>
                {/* Never a blank cell: an empty "removed by" reads as "nobody", which is a
                    claim — and the opposite one from "we have no record of who did this".
                    Same decision, same words, as the restore dialog's provenance column. */}
                <td>
                  {r.prunedBy || r.prunedAt ? (
                    <>
                      <div>{r.prunedBy || 'unknown operator'}</div>
                      <div className="atlas-sub muted">{timeAgo(r.prunedAt)}</div>
                      {r.prunedReason && <div className="atlas-sub">{r.prunedReason}</div>}
                    </>
                  ) : (
                    <span className="atlas-sub muted">no removal on record</span>
                  )}
                </td>
                {/* The interval between the two stamps is the story: minutes means the
                    removal was undone before anyone could act on it, days means a node
                    came back. Both are shown, so the reader does the subtraction. */}
                <td className="muted">{timeAgo(r.supersededAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
